import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Button, Box, Typography } from '@mui/material';
import {
    ShowChart as ShowChartIcon,
    Inventory2 as Inventory2Icon,
    Straighten as StraightenIcon,
    History as HistoryIcon,
    MenuBook as MenuBookIcon,
    CompareArrows as CompareArrowsIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import VersionStatusBadge from './VersionStatusBadge';
import AuthControls from './AuthControls';
import { useTier } from '../useTier';
import { AUTH_ENABLED } from '../authConfig';

// NAV_ITEMS drives the primary navigation. `paid` items (MSA / History / Compare —
// ADR-007) show a small lock for free/anonymous users once auth is on (AUTH-7); the
// route-level TierGate is the actual boundary. Free when off → no locks (byte-for-byte).
const NAV_ITEMS = [
    { label: 'Cockpit',       path: '/',              icon: <ShowChartIcon sx={{ fontSize: 18 }} /> },
    // TIER-3 Phase 1 (design doc §5, owner decision 7): sibling tab next to
    // Cockpit/MSA/History — never the signed-in landing page (Cockpit stays home).
    { label: 'Parts',         path: '/parts',         icon: <Inventory2Icon sx={{ fontSize: 18 }} />, paid: true },
    { label: 'MSA',           path: '/msa',           icon: <StraightenIcon sx={{ fontSize: 18 }} />, paid: true },
    { label: 'History',       path: '/history',       icon: <HistoryIcon sx={{ fontSize: 18 }} />, paid: true },
    { label: 'Compare',       path: '/compare',       icon: <CompareArrowsIcon sx={{ fontSize: 18 }} />, paid: true },
    { label: 'Documentation', path: '/documentation', icon: <MenuBookIcon sx={{ fontSize: 18 }} /> },
];

function NavigationBar() {
    const location = useLocation();
    // useTier is flag-bound: the disabled build returns a stable free tier without
    // touching Clerk, so this is a safe no-op when auth is off.
    const { isPaid, isLoaded } = useTier();
    const showLock = (item) => AUTH_ENABLED && isLoaded && !isPaid && !!item.paid;

    // Exact match for the home route; prefix match for nested sub-paths.
    const isActive = (path) =>
        path === '/'
            ? location.pathname === '/'
            : location.pathname === path || location.pathname.startsWith(path + '/');

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={{
                backgroundColor: 'rgba(18, 18, 18, 0.92)',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(8px)',
            }}
        >
            <Toolbar variant="dense" sx={{ gap: 0.5, minHeight: 48 }}>
                <Typography
                    component={RouterLink}
                    to="/"
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        color: 'rgba(255,255,255,0.92)',
                        textDecoration: 'none',
                        mr: 2,
                        whiteSpace: 'nowrap',
                        '&:hover': { color: '#90caf9' },
                    }}
                >
                    Capability Cockpit
                </Typography>

                <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5 }}>
                    {NAV_ITEMS.map((item) => {
                        const { label, path, icon } = item;
                        const active = isActive(path);
                        const locked = showLock(item);
                        return (
                            <Button
                                key={path}
                                component={RouterLink}
                                to={path}
                                startIcon={icon}
                                endIcon={locked ? <LockIcon sx={{ fontSize: 13, opacity: 0.6 }} /> : undefined}
                                title={locked ? `${label} is a premium feature` : undefined}
                                size="small"
                                sx={{
                                    color: active ? '#90caf9' : 'rgba(255,255,255,0.65)',
                                    fontWeight: active ? 600 : 400,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    px: 1.5,
                                    borderBottom: active ? '2px solid #90caf9' : '2px solid transparent',
                                    '&:hover': {
                                        color: '#90caf9',
                                        backgroundColor: 'rgba(144,202,249,0.06)',
                                    },
                                }}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </Box>
                <VersionStatusBadge />
                {/* AUTH-4: Sign in / UserButton — renders nothing when auth is off. */}
                <AuthControls />
            </Toolbar>
        </AppBar>
    );
}

export default NavigationBar;
